import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { COLORS } from "@ts/constants";
import { ITrendsComparision, MyProductTrendsModules } from "@ts/common-types";
import WidgetCard from "./WidgetCard";
import { titleCase } from "@/ts/utils";

const TrendsComparisionWidget: React.FC<ITrendsComparision & {
    selectedCompareOption?: string; 
    oppositeYAxis?: string; 
    yAxis?: string
    compareWithOptions?: Array<{[key: string]: string }> 
}> = ({
    widgetData,
    title,
    isTitleRequired = true,
    isCardRequired = true,
    graphAttributes = {},
    selectedCompareOption,
    yAxis,
    oppositeYAxis,
    compareWithOptions
}) => {
    const chartRefs = React.useRef([]);

    let series = widgetData?.chartDataSeries?.series || [];

    if (
        selectedCompareOption === MyProductTrendsModules.COMPARISON &&
        oppositeYAxis &&
        yAxis &&
        Array.isArray(series)
    ) {
        const validOptions = compareWithOptions
            ?.map((opt) => opt.value?.toLowerCase())
            .filter(Boolean);

        series = series
            ?.map((s) => {
                if (s?.name && validOptions?.includes(s.name.toLowerCase())) {
                    if (s.name.toLowerCase() === yAxis.toLowerCase()) {
                        return { ...s, yAxis: 0 };
                    }

                    if (s.name.toLowerCase() === oppositeYAxis.toLowerCase()) {
                        return { ...s, yAxis: 1 };
                    }
                    return null;
                }
                return s;
            })
            ?.filter(Boolean);
    }

    const getChartOptions = (categories: string[], series: any[]) => {
        // If oppositeYAxisTitle is provided, create two y-axes.
        const yAxis = graphAttributes.oppositeYAxisTitle
            ? [
                  {
                      title: {
                          text: graphAttributes.yAxisTitle || "#Reviews",
                          align: "middle",
                      },
                      labels: { overflow: "justify" },
                      gridLineWidth: 1,
                      lineWidth: 1,
                  },
                  {
                      title: {
                          text: graphAttributes.oppositeYAxisTitle,
                          align: "middle",
                      },
                      labels: { overflow: "justify" },
                      gridLineWidth: 1,
                      lineWidth: 1,
                      opposite: true,
                  },
              ]
            : {
                  title: {
                      text: graphAttributes.yAxisTitle || "#Reviews",
                      align: "middle",
                  },
                  labels: { overflow: "justify" },
                  gridLineWidth: 1,
                  lineWidth: 1,
              };

        return {
            chart: {
                type: "line",
                responsive: true,
            },
            colors: graphAttributes.colors || COLORS,
            title: { text: graphAttributes.title || "" },
            xAxis: {
                categories,
                title: { text: "Dates" },
                lineWidth: 1,
            },
            yAxis,
            plotOptions: {
                line: {
                    dataLabels: { enabled: false },
                    enableMouseTracking: true,
                },
            },
            legend: {
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
                backgroundColor:
                    Highcharts.defaultOptions.legend.backgroundColor || "#FFFFFF",
                labelFormatter: function (this: Highcharts.Series) {
                    return titleCase(this.name);
                },

            },
            credits: { enabled: false },
            ...(graphAttributes.formatter
                ? {
                      tooltip: {
                          formatter: graphAttributes.formatter,
                          useHTML: true,
                      },
                  }
                : {}),
            series,
            lang: { noData: "No data to display" },
            noData: {
                style: {
                    fontWeight: "bold",
                    fontSize: "15px",
                    color: "#303030",
                },
            },
            responsive: {
                rules: [
                    {
                        condition: { maxWidth: 500 },
                        chartOptions: {
                            legend: {
                                align: "center",
                                verticalAlign: "bottom",
                                layout: "horizontal",
                            },
                        },
                    },
                ],
            },
        };
    };

    return (
        <WidgetCard
            overwriteDefaultClass={!isCardRequired}
            className={
                !isCardRequired
                    ? "bg-transparent border-0 rounded-0 shadow-none"
                    : ""
            }
        >
            {isTitleRequired && (
                <h5 className="mb-3">
                    {title || "#Review Trends on Amazon vs Others"}
                </h5>
            )}
            <HighchartsReact
                highcharts={Highcharts}
                options={getChartOptions(
                    widgetData?.chartDataSeries?.categories || [],
                    series
                )}
                ref={(el) => (chartRefs.current[1] = el)}
            />
        </WidgetCard>
    );
};

export default TrendsComparisionWidget;
